import React, { useEffect, useState } from 'react';

export interface ToastProps {
  type?: 'success' | 'error' | 'warning' | 'info';
  message: string;
  onClose?: () => void;
  duration?: number;
}

export const Toast = ({ type = 'info', message, onClose, duration = 3000 }: ToastProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!isVisible) return null;

  const typeConfig = {
    success: { icon: 'ri-checkbox-circle-fill', color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
    error: { icon: 'ri-error-warning-fill', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
    warning: { icon: 'ri-alert-fill', color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
    info: { icon: 'ri-information-fill', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  };

  const config = typeConfig[type];

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg z-[100] transition-transform ${config.bg} ${config.border}`}>
      <i className={`${config.icon} ${config.color} text-xl`}></i>
      <span className="text-sm font-medium text-gray-800">{message}</span>
      <button onClick={() => { setIsVisible(false); if(onClose) onClose(); }} className="ml-2 text-gray-400 hover:text-gray-600 cursor-pointer">
        <i className="ri-close-line"></i>
      </button>
    </div>
  );
};

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = React.useCallback((type: ToastType, message: string, duration?: number) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message, duration }]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <Toast type={t.type} message={t.message} duration={t.duration} onClose={() => removeToast(t.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');

  const toast = {
    success: (message: string, duration?: number) => ctx.addToast('success', message, duration),
    error: (message: string, duration?: number) => ctx.addToast('error', message, duration),
    warning: (message: string, duration?: number) => ctx.addToast('warning', message, duration),
    info: (message: string, duration?: number) => ctx.addToast('info', message, duration),
  };

  return { toast, toasts: ctx.toasts, removeToast: ctx.removeToast };
}
